import React from 'react';
import { NULL_ADDRESS } from '@soundxyz/utils/constants/constants';
import { HeyLogo } from '@components/icons/Social/Hey';
import { TelegramLogo } from '@components/icons/Social/Telegram';
import { TwitterLogo } from '@components/icons/Social/Twitter';
import { WarpcastLogo } from '@components/icons/Social/Warpcast';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { usePrefetchEmbed } from '@hooks/usePrefetchEmbed';
import { trackClickEvent } from '@lib/analytics';
import { getSocialShareMessage, getUrlPathForTwitter } from '@utils/helpers';
import { REGULAR_DROP_NO_URL_SHARE_TEXT, REGULAR_PLAYLIST_NO_URL_SHARE_TEXT } from './helpers';

export const SocialShare = ({
  associationType,
  id,
  url,
  referralAddress,
  messageType,
}: {
  associationType: 'Release' | 'Playlist' | 'Post';
  messageType: 'drop' | 'playlist';
  id: string;
  url: string;
  referralAddress: `0x${string}` | null;
}) => {
  const shareMessage = getSocialShareMessage({
    type: messageType,
    url,
  });

  const onTwitterShareClick = () => {
    if (associationType === 'Release') {
      trackClickEvent({
        event: 'Share to Twitter Clicked',
        properties: {
          associationId: id,
          associationType,
          referralAddress: (referralAddress ?? NULL_ADDRESS).toLowerCase(),
          sourceComponent: 'Social Share',
        },
      });
    } else {
      trackClickEvent({
        event: 'Share Modal Third Party Share',
        properties: {
          associationType,
          associationId: id,
          sourceComponent: 'Song Card',
          thirdParty: 'Twitter',
        },
      });
    }
  };

  const onSocialClick = (thirdParty: string) => {
    trackClickEvent({
      event: 'Share Modal Third Party Share',
      properties: {
        associationType,
        associationId: id,
        sourceComponent: 'Song Card',
        thirdParty,
      },
    });
  };

  usePrefetchEmbed({
    releaseId: id,
  });

  return (
    <div className="mx-auto flex gap-4">
      <AccessibleLink
        absoluteUri={getUrlPathForTwitter({ type: messageType, url })}
        onClick={onTwitterShareClick}
        type="external"
        aria-label="Share to X (Twitter)"
      >
        <TwitterLogo className="cursor-pointer transition-all ease-in-out hover:brightness-[116%]" />
      </AccessibleLink>
      <AccessibleLink
        absoluteUri={`https://warpcast.com/~/compose?text=${shareMessage}`}
        onClick={() => onSocialClick('Warpcast')}
        type="external"
        aria-label="Share to Warpcast"
      >
        <WarpcastLogo className="cursor-pointer transition-all ease-in-out hover:brightness-[116%]" />
      </AccessibleLink>
      <AccessibleLink
        absoluteUri={`https://lenster.xyz/?text=${shareMessage}`}
        onClick={() => onSocialClick('Hey.xyz')}
        type="external"
        aria-label="Share to Hey"
      >
        <HeyLogo className="cursor-pointer transition-all ease-in-out hover:brightness-[116%]" />
      </AccessibleLink>
      <AccessibleLink
        absoluteUri={`https://t.me/share/url?url=${url}&text=${
          messageType === 'drop'
            ? REGULAR_DROP_NO_URL_SHARE_TEXT
            : REGULAR_PLAYLIST_NO_URL_SHARE_TEXT
        }`}
        onClick={() => onSocialClick('Telegram')}
        type="external"
        aria-label="Share to Telegram"
      >
        <TelegramLogo className="cursor-pointer transition-all ease-in-out hover:brightness-[116%]" />
      </AccessibleLink>
    </div>
  );
};
